/// Author line for a dev-blog post or comment: display name, a
/// `✦ Developer` badge for the developer's own posts, a relative
/// timestamp, and an "(edited)" marker. Developer names render in
/// emerald to reinforce the highlight.

import type { Timestamp } from 'firebase/firestore'
import { displayNameFor } from '../data/devBlog'

function relativeTime(date: Date): string {
  const diffMs = date.getTime() - Date.now()
  const abs = Math.abs(diffMs)
  const MIN = 60_000
  const HR = 60 * MIN
  const DAY = 24 * HR
  if (abs < MIN) return 'just now'
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (abs < HR) return rtf.format(Math.round(diffMs / MIN), 'minute')
  if (abs < DAY) return rtf.format(Math.round(diffMs / HR), 'hour')
  if (abs < 30 * DAY) return rtf.format(Math.round(diffMs / DAY), 'day')
  return date.toLocaleDateString()
}

export function DevBlogByline(props: {
  authorName: string | null
  authorUid: string
  authorIsDeveloper: boolean
  createdAt: Timestamp | null
  editedAt: Timestamp | null
}) {
  const name = displayNameFor({ authorName: props.authorName, authorUid: props.authorUid })
  const when = props.createdAt ? relativeTime(props.createdAt.toDate()) : 'just now'
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
      <span className={props.authorIsDeveloper ? 'font-semibold text-emerald-300' : 'font-medium text-slate-200'}>
        {name}
      </span>
      {props.authorIsDeveloper && (
        <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300 ring-1 ring-emerald-400/30">
          ✦ Developer
        </span>
      )}
      <span className="text-slate-500">·</span>
      <span className="text-slate-500">{when}</span>
      {props.editedAt && <span className="text-slate-600">(edited)</span>}
    </div>
  )
}
